import React, { FC, useEffect, useState, useRef } from "react";
import { Button, Container, Dimmer, Grid, Icon, Loader, Segment, SemanticCOLORS } from "semantic-ui-react";
import { SemanticICONS } from "semantic-ui-react/dist/commonjs/generic";
import { DragMoveEvent } from "@dnd-kit/core";

import { api, useApi } from "../../../api/api";
import { ApiFile } from "../../../api/io";
import { ActionAccess } from "../../../api/base";
import MessageWindow from "../../../components/MessageWindow";
import FileWindow from "../../../components/FileWindow";

import Arrow from "./Arrow";
import SortableList, { SortableSegment } from "./SortableList";
import RenameInput from "./RenameInput";
import { getDecodeOutputSQSValues, getUniqueSQSValues, jsonsEqual, buttonInSegment } from "./utils";

export interface SqsConfigContent {
    [key: string]: string[];
}

const tmpKey = "tmp";

const SQSConfig: FC = () => {
    const configsApi = useApi(api.configs);
    const requestsApi = useApi(api.requests);

    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState<any>("");
    const [currentFile, setFile] = useState<ApiFile>();

    const [sqsConfig, setSqsConfig] = useState<any>();
    const [sqsConfigContent, setSqsConfigContent] = useState<SqsConfigContent>({});
    const sqsConfigContentOrigin = useRef<SqsConfigContent>({});
    const [sqsConfigContentString, setSqsConfigContentString] = useState<string>("");
    const [, setDragMove] = useState<DragMoveEvent>();

    const [hoveredSegment, setHoveredSegment] = useState<string | null>(null);
    const [renameSegment, setRenameSegment] = useState<string | null>(null);
    const [plusSegment, setPlusSegment] = useState<string | null>(null);
    const replacedVenues = useRef<Set<string>>(new Set());

    const [isMoving, setIsMoving] = useState<boolean>(false);

    useEffect(() => {
        load();
    }, []);

    const load = async () => {
        setLoading(true);
        setRenameSegment(null);
        setIsMoving(false);
        setPlusSegment(null);
        return configsApi
            .getSqsConfig()
            .then(({ item, content, jsonContent }) => {
                setSqsConfig(item);
                setSqsConfigContent(jsonContent);
                sqsConfigContentOrigin.current = { ...jsonContent };
                setSqsConfigContentString(content);
            })
            .catch((e) => console.error(e))
            .finally(() => setLoading(false));
    };

    const getVenueValues = Object.keys(sqsConfigContent).filter((item) => item !== tmpKey);
    const getSqsValues = getUniqueSQSValues(sqsConfigContent);

    const replaceVenueValue = (venueValue: string, venueIndex: number) => {
        for (let value of getVenueValues.slice(0, venueIndex)) {
            if (venueValue.includes(value)) {
                replacedVenues.current.add(venueValue);
                return `Replaced by: ${value}`;
            }
        }
        replacedVenues.current.delete(venueValue);
        return null;
    };

    const removeTemporaryKeyIfEmpty = () => {
        if (tmpKey in sqsConfigContent) {
            if (sqsConfigContent[tmpKey].length === 0) {
                delete sqsConfigContent[tmpKey];
            }
        }
    };

    const appendValueInTemporaryKey = (values: string[]) => {
        for (const value of values) {
            const exists = Object.values(sqsConfigContent).some((array) => array.includes(value));
            if (!exists) {
                if (tmpKey in sqsConfigContent) {
                    sqsConfigContent[tmpKey].push(value);
                } else {
                    sqsConfigContent[tmpKey] = [value];
                }
            }
        }
    };

    const onSave = async () => {
        configsApi
            .saveSqsConfig(sqsConfigContent)
            .then(() => {})
            .catch((e) => console.error(e))
            .finally(() => load().then(() => setMessage("request submitted")));
    };

    const onDelete = (element: string) => {
        if (element in sqsConfigContent) {
            // delete venue value
            const sqsValues = sqsConfigContent[element];
            delete sqsConfigContent[element];
            replacedVenues.current.delete(element);
            appendValueInTemporaryKey(sqsValues);
        } else {
            // delete SQS value
            const elementsToRemove = getDecodeOutputSQSValues(element);
            for (const key in sqsConfigContent) {
                sqsConfigContent[key] = sqsConfigContent[key].filter((item) => !elementsToRemove.includes(item));
            }
            removeTemporaryKeyIfEmpty();
        }
        setSqsConfigContent({ ...sqsConfigContent });
    };

    const onEdit = (element: string) => {
        setRenameSegment(element);
        setIsMoving(true);
    };

    const onRename = (content: SqsConfigContent) => {
        setSqsConfigContent(content);
        setIsMoving(false);
        setRenameSegment(null);
    };

    const onMinus = (venueValue: string, sqsValue: string) => {
        const sqsValues = getDecodeOutputSQSValues(sqsValue);
        sqsConfigContent[venueValue] = sqsConfigContent[venueValue].filter((item) => !sqsValues.includes(item));
        appendValueInTemporaryKey(sqsValues);
        setSqsConfigContent({ ...sqsConfigContent });
        setPlusSegment(null);
    };

    const onPlus = (venueValue: string, sqsValue: string) => {
        const sqsValues = getDecodeOutputSQSValues(sqsValue);
        sqsConfigContent[venueValue] = sqsConfigContent[venueValue].concat(sqsValues);
        if (tmpKey in sqsConfigContent) {
            sqsConfigContent[tmpKey] = sqsConfigContent[tmpKey].filter((item) => !sqsValues.includes(item));
        }
        removeTemporaryKeyIfEmpty();
        setSqsConfigContent({ ...sqsConfigContent });
        setPlusSegment(null);
    };

    const onAddVenue = () => {
        sqsConfigContent["Rename venue"] = [];
        setSqsConfigContent({ ...sqsConfigContent });
        setRenameSegment("Rename venue");
        setIsMoving(true);
    };

    const onAddSQS = () => {
        if (tmpKey in sqsConfigContent) {
            sqsConfigContent[tmpKey].push(...getDecodeOutputSQSValues("Rename SQS.fifo"));
        } else {
            sqsConfigContent[tmpKey] = getDecodeOutputSQSValues("Rename SQS.fifo");
        }
        setSqsConfigContent({ ...sqsConfigContent });
        setRenameSegment("Rename SQS.fifo");
        setIsMoving(true);
    };

    const isSaveDisabled = () => {
        const requestNotAllowed = requestsApi.getAccessLevel("") !== ActionAccess.ALLOWED;
        const hasNotConnectedSQS = tmpKey in sqsConfigContent;
        const hasNotConnectedVenue = Object.values(sqsConfigContent).some((array) => array.length === 0);
        const contentNotChanged = jsonsEqual(sqsConfigContent, sqsConfigContentOrigin.current);
        const hasReplacedVenue = Boolean(replacedVenues.current.size);

        return requestNotAllowed || hasNotConnectedVenue || hasNotConnectedSQS || contentNotChanged || hasReplacedVenue;
    };

    const isExistArrow = (sqsValue: string) => {
        if (plusSegment) {
            return sqsConfigContent[plusSegment].some((sqsValues) =>
                getDecodeOutputSQSValues(sqsValue).some((sqsValue) => sqsValues === sqsValue),
            );
        }
        return false;
    };

    const isReplaced = (value: string, index: number) => {
        return getVenueValues.slice(0, index).some((item) => value.includes(item));
    };

    const isVenueRed = (value: string, index: number) => {
        if (sqsConfigContent[value].length === 0) {
            return true;
        }
        return isReplaced(value, index);
    };

    const isSQSRed = (value: string) => {
        if (tmpKey in sqsConfigContent) {
            return getDecodeOutputSQSValues(value).every((element) => sqsConfigContent[tmpKey].includes(element));
        }
        return false;
    };

    const connectionButton = (
        onClick: () => any,
        icon: SemanticICONS,
        corner: "right" | "left",
        color?: SemanticCOLORS,
    ) => (
        <Button
            circular
            icon
            color={color}
            style={{
                position: "absolute",
                top: "50%",
                zIndex: 1000,
                padding: "6px",
                fontSize: "10px",
                transform: `translate(${corner === "right" ? "50%" : "-50%"}, -50%)`,
                [corner === "right" ? "right" : "left"]: 0,
            }}
            onClick={onClick}
        >
            <Icon name={icon} />
        </Button>
    );

    const addSegment = (onClick: () => void, width?: string) => (
        <div style={{ padding: "2px 0" }}>
            <Segment
                onClick={onClick}
                style={{
                    cursor: "pointer",
                    textAlign: "center",
                    background: "#c1e7f3",
                    ...(width && { width, minWidth: "300px" }),
                }}
            >
                <Icon name="add"></Icon>
            </Segment>
        </div>
    );

    const renderSegmentConnection = (sqsValue: string) => {
        if (plusSegment) {
            return isExistArrow(sqsValue)
                ? connectionButton(() => onMinus(plusSegment, sqsValue), "minus", "left", "red")
                : connectionButton(() => onPlus(plusSegment, sqsValue), "plus", "left", "green");
        }
    };

    const renderVenueSortableList = () => {
        const longestText = getVenueValues.reduce(
            (longest, text) => (text.length > longest.length ? text : longest),
            "",
        );
        const segmentWidth = `${longestText.length * 12 + (longestText.length > 31 ? 15 : 50)}px`;
        return (
            <>
                <SortableList
                    onDragMove={setDragMove}
                    onChange={setSqsConfigContent}
                    content={sqsConfigContent}
                    items={getVenueValues}
                    setIsMoving={setIsMoving}
                >
                    {getVenueValues.map((venueValue, venueIndex) => (
                        <SortableSegment
                            id={venueValue}
                            setHovered={setHoveredSegment}
                            width={segmentWidth}
                            isRed={isVenueRed(venueValue, venueIndex)}
                        >
                            {renameSegment === venueValue ? (
                                <RenameInput
                                    index={venueIndex}
                                    value={venueValue}
                                    content={sqsConfigContent}
                                    setContent={onRename}
                                />
                            ) : (
                                venueValue
                            )}
                            {hoveredSegment === venueValue && !isMoving && (
                                <div style={{ position: "absolute", right: 0, paddingRight: "15px" }}>
                                    {buttonInSegment("edit", () => onEdit(venueValue))}
                                    {buttonInSegment("remove", () => onDelete(venueValue))}
                                    {connectionButton(() => setPlusSegment(venueValue), "plus", "right")}
                                </div>
                            )}
                            <span className="span-label">{replaceVenueValue(venueValue, venueIndex)}</span>
                            {plusSegment === venueValue &&
                                connectionButton(() => setPlusSegment(null), "check", "right", "green")}
                        </SortableSegment>
                    ))}
                </SortableList>
                {addSegment(onAddVenue, segmentWidth)}
            </>
        );
    };

    const renderSQSSegments = () => {
        return (
            <>
                {getSqsValues.map((sqsValue, sqsIndex) => (
                    <Segment
                        id={sqsValue}
                        onMouseEnter={() => setHoveredSegment(sqsValue)}
                        onMouseLeave={() => setHoveredSegment(null)}
                        style={{ margin: "4px 0", ...(isSQSRed(sqsValue) && { borderColor: "red" }) }}
                    >
                        <div style={{ display: "flex", alignItems: "center" }}>
                            {renameSegment === sqsValue ? (
                                <RenameInput
                                    index={sqsIndex}
                                    value={sqsValue}
                                    content={sqsConfigContent}
                                    setContent={onRename}
                                    withFifo={true}
                                />
                            ) : (
                                sqsValue.replace(/\.fifo$/, "")
                            )}
                            {hoveredSegment === sqsValue && !isMoving && (
                                <div style={{ position: "absolute", right: 0, paddingRight: "15px" }}>
                                    {buttonInSegment("edit", () => onEdit(sqsValue))}
                                    {buttonInSegment("info", () => setMessage(getDecodeOutputSQSValues(sqsValue)))}
                                    {buttonInSegment("remove", () => onDelete(sqsValue))}
                                </div>
                            )}
                            {sqsValue.endsWith(".fifo") && <span className="span-label">fifo</span>}
                            {renderSegmentConnection(sqsValue)}
                        </div>
                    </Segment>
                ))}
                {addSegment(onAddSQS)}
            </>
        );
    };

    const renderArrows = () => {
        return getSqsValues.map((sqsValue, sqsIndex) =>
            getVenueValues.map(
                (venueValue, venueIndex) =>
                    sqsConfigContent[venueValue].some((sqsValues) =>
                        getDecodeOutputSQSValues(sqsValue).some((sqsValue) => sqsValues === sqsValue),
                    ) && (
                        <Arrow
                            startElementId={venueValue}
                            endElementId={sqsValue}
                            isReplaced={isReplaced(venueValue, venueIndex)}
                        />
                    ),
            ),
        );
    };

    const clickOutsideRenameSegment = (e: MouseEvent) => {
        if (renameSegment) {
            const element = document.getElementById(renameSegment);
            if (element && !element.contains(e.target as Node)) {
                setIsMoving(false);
                setRenameSegment(null);
            }
        }
    };

    return (
        <Container onClick={(event: MouseEvent) => clickOutsideRenameSegment(event)}>
            {message && <MessageWindow message={message} onClose={() => setMessage("")} />}

            <Button circular onClick={() => setFile({ name: sqsConfig.key, content: sqsConfigContentString })}>
                View config file
            </Button>
            {currentFile && <FileWindow file={currentFile} onClose={() => setFile(undefined)} />}

            <Button
                circular
                onClick={() => load()}
                disabled={jsonsEqual(sqsConfigContent, sqsConfigContentOrigin.current)}
            >
                Restore
            </Button>

            <Button circular onClick={onSave} disabled={isSaveDisabled()}>
                <Icon name="save" />
                Save
            </Button>

            {sqsConfig && (
                <p>
                    Last uploaded by: {sqsConfig.author} ({sqsConfig.created_at.split(".")[0]})
                </p>
            )}

            {loading ? (
                <Dimmer inverted active={true}>
                    <Loader />
                </Dimmer>
            ) : (
                <Grid columns={3}>
                    <Grid.Column width={9}>{renderVenueSortableList()}</Grid.Column>
                    <Grid.Column width={1}></Grid.Column>
                    <Grid.Column>{renderSQSSegments()}</Grid.Column>
                    {renderArrows()}
                </Grid>
            )}
        </Container>
    );
};

export default SQSConfig;
